import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import Seo from "../components/site/Seo";
import { resolveImageUrl } from "../components/admin/ImageUpload";

export default function BlogDetail() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blogs/${id}`);
        setBlog(data);
      } catch {
        setNotFound(true);
        return;
      }
      try {
        const { data } = await api.get("/blogs");
        setRelated(data.filter((b) => b.id !== id).slice(0, 3));
      } catch { /* ignore */ }
    })();
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Seo title="Article not found" />
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center">
          <div className="section-overline justify-center inline-flex">Insights</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold mt-4 text-[#0F172A]">Article not found</h1>
          <p className="mt-4 text-[#475569]">The article you are looking for may have been moved or unpublished.</p>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2563EB] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wide hover:bg-[#1D4ED8]">
            <ArrowLeft size={16} /> Back to home
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return <div className="min-h-screen grid place-items-center text-[#475569]">Loading...</div>;
  }

  const cover = resolveImageUrl(blog.cover_image);

  return (
    <div data-testid="blog-detail-page" className="bg-white">
      <Seo title={blog.title} description={blog.excerpt} image={cover} type="article" path={`/insights/${blog.id}`} />
      <Navbar />

      <main>
        <section className="relative pt-40 pb-20 bg-azure-mesh overflow-hidden">
          <div className="absolute inset-0 bg-grid-blue opacity-50" />
          <div className="relative max-w-4xl mx-auto px-6 md:px-12">
            <Link to="/#insights" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#2563EB]">
              <ArrowLeft size={14} /> Back to Insights
            </Link>
            <div className="mt-6 section-overline">{blog.category}</div>
            <h1 className="font-display text-4xl md:text-6xl font-semibold mt-4 leading-[1.05] text-balance text-[#0F172A]">
              {blog.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-[#475569]">
              <span className="inline-flex items-center gap-2"><User size={14} className="text-[#2563EB]" /> {blog.author}</span>
              {blog.published_at && (
                <span className="inline-flex items-center gap-2"><Calendar size={14} className="text-[#2563EB]" /> {blog.published_at}</span>
              )}
            </div>
          </div>
        </section>

        {cover && (
          <div className="max-w-5xl mx-auto px-6 md:px-12 -mt-12 relative z-10">
            <div className="rounded-2xl overflow-hidden border border-blue-100 shadow-[0_30px_80px_-40px_rgba(37,99,235,0.4)]">
              <img src={cover} alt={blog.title} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
            </div>
          </div>
        )}

        <article className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="font-display text-2xl text-[#0F172A] leading-snug italic">{blog.excerpt}</p>
          <div className="dotted-divider my-10" />
          <div className="prose prose-lg max-w-none text-[#0F172A] leading-relaxed whitespace-pre-line">
            {blog.body}
          </div>
        </article>

        {related.length > 0 && (
          <section className="bg-soft-blue py-20">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="section-overline">Continue Reading</div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold mt-4 text-[#0F172A]">More Insights</h2>
              <div className="mt-10 grid md:grid-cols-3 gap-6">
                {related.map((b) => (
                  <Link key={b.id} to={`/insights/${b.id}`} className="group bg-white border border-blue-100 rounded-xl overflow-hidden hover:border-[#2563EB] hover:shadow-lg transition-all">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={resolveImageUrl(b.cover_image)} alt={b.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-5">
                      <div className="overline">{b.category}</div>
                      <div className="mt-2 font-display text-lg text-[#0F172A] line-clamp-2">{b.title}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
